import { Component, OnInit, ChangeDetectorRef, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { RolesService } from '../../services/roles.service';
import { UsersService } from '../../services/users.service';
import { CommonService } from '../../services/common.service';
import { AuthService } from '../../services/auth.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-roles',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, HeaderComponent],
  templateUrl: './roles.component.html',
  styleUrls: []
})
export class RolesComponent implements OnInit {
  copyrightDetails: any;

  // --- Loading / UI State ---
  isLoaded = false;
  isRefreshTable = false;
  refreshTableText = '';
  isExistingUsersLoading = false;
  isAvailableUsersLoading = false;
  isLoadingPermission = false;
  isLoadingAccessModules = false;
  isLoadingWorkbench = false;
  isLoadingFeatureList = false;
  isProcessed = true;
  isUsersLoaded = true;
  isUpdating = false;

  // --- Roles Table ---
  accessRolesList: any[] = [];
  totalRecords = 0;
  sortType = 'role';
  sortReverse = false;
  search = '';
  currentPage = 1;
  pageSize = 10;
  selectedRow: any = null;

  // --- Column Visibility ---
  isColumnSettingsVisible = false;
  columnVisibility: { [key: string]: boolean } = {
    role: true,
    code: true,
    details: true,
    view: true,
    clone: true
  };

  // --- Role Details Panel ---
  isShowRoleDetails = false;
  role_information: any = {};
  userCountForRole = 0;

  // --- Tabs ---
  isSummary = false;
  isShowPermission = false;
  isModules = false;
  isWorkbench = false;
  isFeatureModule = false;

  // --- Summary Tab: Users ---
  userRoles: any[] = [];
  availableUsers: any[] = [];
  users: any[] = [];
  existingUserSearch = '';
  availableUserSearch = '';
  existingUserSortType = 'name';
  existingUserSortReverse = false;
  availableUserSortType = 'name';
  availableUserSortReverse = false;

  // --- Permissions Tab ---
  assigned_permissions: any[] = [];
  role_permissions: any[] = [];
  currentIndex: any = null;
  permissionSearch = '';
  permissionSortReverse = false;

  // --- Cart Permissions ---
  assigned_cart_permissions: any[] = [];
  cart_role_permissions: any[] = [];
  cart_message: string | null = null;
  cart_errorMessage: string | null = null;

  // --- Access Modules Tab ---
  access_modules: any[] = [];
  accessModulesList: any[] = [];
  modulesSortType = 'module_id';
  modulesSortReverse = false;
  accessModuleSearch = '';
  showNotificationAboutUserSession = false;

  // --- Access Workbench Tab ---
  accessWorkbenchList: any[] = [];
  workbenchSortType = 'name';
  workbenchSortReverse = false;
  accessWorkbenchSearch = '';

  // --- Feature Permission Tab ---
  featuresList: any[] = [];
  featureAccessMap: any = {};
  featureSearch = '';
  featureSortReverse = false;

  // --- Create Role Form ---
  isShowRole = false;
  isCreateRole = false;
  role: any = {};
  saveBtnText = 'Save';
  saveBtnError = false;
  isSaveSuccess = false;

  // --- Clone Role Form ---
  isCloneRole = false;
  old_role_id: any = null;
  cloneBtnTxt = 'Clone';
  cloneBtnError = false;
  isCloneSuccess = false;

  // --- Update Role Form ---
  isUpdateRole = false;
  roleDetails: any = null;
  oldRoleDetails: any = null;
  updateBtnText = 'Update';
  updateBtnError = false;
  isUpdateSuccess = false;
  updateUserMessage: string | null = null;

  // --- Delete Role ---
  enableDeleteUser = false;
  roleInfo: any = null;
  roleDeleteErrorMessage: string | null = null;
  hasRoleDeleted = false;

  // --- Messages ---
  message: string | null = null;
  errorMessage: string | null = null;

  // --- store_manager flag ---
  storeManager: any = null;

  private platformId = inject(PLATFORM_ID);

  constructor(
    private rolesService: RolesService,
    private usersService: UsersService,
    private common: CommonService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit(): Promise<void> {

    this.isColumnSettingsVisible = false;

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (typeof window !== 'undefined') {
      const savedDetails = sessionStorage.getItem('copyrightDetails');
      if (savedDetails) {
        this.copyrightDetails = JSON.parse(savedDetails);
      }
    }
    try {
      const smStr = this.common.SessionMemory.API.Get('store_manager');
      if (smStr && smStr !== 'undefined') {
        this.storeManager = JSON.parse(smStr);
      } else {
        this.storeManager = null;
      }
    } catch (e) {
      this.storeManager = null;
    }

    // Wait for auth to be ready before loading data
    try {
      await firstValueFrom(
        toObservable(this.authService.user).pipe(
          filter(u => !!u)
        )
      );
      this.getAccessRoles(undefined, false);
    } catch {
      this.getAccessRoles(undefined, false);
    }
  }

  // ========================================================================
  // ROLES TABLE
  // ========================================================================

  getAccessRoles(refresh?: boolean, isCache?: boolean, showDetailsId?: any): void {
    if (refresh !== undefined && refresh) {


      this.search='';
      // Reset column visibility to all true when refreshing
      this.columnVisibility = {
        role: true,
        code: true,
        details: true,
        view: true,
        clone: true
      };
      this.totalRecords = 0;
      this.isRefreshTable = true;
      this.isShowRoleDetails = false;
      this.isShowPermission = false;
      this.isModules = false;
      this.isWorkbench = false;
      this.isFeatureModule = false;
      this.isSaveSuccess = false;
      this.enableDeleteUser = false;
      this.refreshTableText = 'Table is refreshing...';

      // Reset to first page when refreshing
      this.currentPage = 1;
    }

    this.isLoaded = false;

    this.rolesService.GetAccessRoles()
      .then((response: any) => {
        this.accessRolesList = JSON.parse(JSON.stringify(response.data || response));
        this.totalRecords = this.accessRolesList.length;
        this.isLoaded = true;

        if (refresh) {
          this.refreshTableText = 'Successfully refreshed';
          setTimeout(() => {
            this.isRefreshTable = false;
            this.cdr.detectChanges();
          }, 3500);
        }

        this.updateTableInformation(1);

        if (showDetailsId != undefined && showDetailsId != null) {
          const requiredIndex = this.accessRolesList.filter((el: any) => el.id == showDetailsId);
          if (requiredIndex && requiredIndex.length > 0) {
            this.dblClickAction(requiredIndex[0]);
          }
        }
        this.cdr.detectChanges();
      })
      .catch((error: any) => {
        if (refresh) {
          this.isRefreshTable = true;
          this.refreshTableText = 'Unsuccessfull!';
        }
        setTimeout(() => {
          this.isRefreshTable = false;
          this.cdr.detectChanges();
        }, 2500);
        console.error('Error loading access roles:', error);
      });
  }

  // --- Sorting ---
  setSort(type: string): void {
    if (this.sortType === type) {
      this.sortReverse = !this.sortReverse;
    } else {
      this.sortType = type;
      this.sortReverse = false;
    }
  }

  getFilteredRoles(): any[] {
    let filtered = this.accessRolesList;
    if (this.search) {
      const term = this.search.toLowerCase();
      filtered = filtered.filter((role: any) =>
        (role.role && role.role.toLowerCase().includes(term)) ||
        (role.code && role.code.toLowerCase().includes(term)) ||
        (role.details && role.details.toLowerCase().includes(term))
      );
    }
    // Sort
    filtered = [...filtered].sort((a: any, b: any) => {
      const valA = a[this.sortType] ? String(a[this.sortType]).toLowerCase() : '';
      const valB = b[this.sortType] ? String(b[this.sortType]).toLowerCase() : '';
      if (valA < valB) return this.sortReverse ? 1 : -1;
      if (valA > valB) return this.sortReverse ? -1 : 1;
      return 0;
    });
    return filtered;
  }

  getPaginatedRoles(): any[] {
    const filtered = this.getFilteredRoles();
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return filtered.slice(startIndex, startIndex + this.pageSize);
  }

  getTotalFilteredCount(): number {
    return this.getFilteredRoles().length;
  }

  getTotalPages(): number {
    return Math.ceil(this.getTotalFilteredCount() / this.pageSize);
  }

  getVisibleCount(): number {
    return Math.min(this.currentPage * this.pageSize, this.getTotalFilteredCount());
  }

  pageChange(page: number): void {
    this.currentPage = page;
  }

  pageChangeHandler(num: number): void {
    this.currentPage = num;
    this.updateTableInformation(num);
  }

  updateTableInformation(currentPage: number): void {
    if (this.totalRecords === 0) {
      return;
    }
    // Pagination info is handled in the template directly
  }

  // --- Row Selection ---
  setClickedRow(roleId: any): void {
    this.selectedRow = roleId;
  }

  dblClickAction(role_information: any): void {
    this.message = null;
    this.errorMessage = null;
    this.role_information = role_information;
    this.isShowRoleDetails = true;
    this.isShowRole = false;
    this.isCloneRole = false;
    this.isUpdateRole = false;
    this.isShowPermission = false;
    this.isModules = false;
    this.isWorkbench = false;
    this.isFeatureModule = false;
    this.isSaveSuccess = false;
    this.loadUsersByRoleId();
  }

  // --- Column Settings ---
  showHideColumnSettings(): void {
    this.isColumnSettingsVisible = !this.isColumnSettingsVisible;
  }

  toggleColumn(col: string): void {
    this.columnVisibility[col] = !this.columnVisibility[col];
  }

  closeRoleDetails(): void {
    this.isShowRoleDetails = false;
    this.selectedRow = null;
    this.existingUserSearch = "";
    this.availableUserSearch = "";
    this.permissionSearch = "";
    this.accessModuleSearch = "";
    this.getAccessRoles(undefined, false);
  }



  // ========================================================================
  // SUMMARY TAB: Users for Role
  // ========================================================================

  loadUsersByRoleId(): void {

    // Clear all search inputs when switching to Summary tab
    this.existingUserSearch = "";
    this.availableUserSearch = "";
    this.permissionSearch = "";
    this.accessModuleSearch = "";
    this.accessWorkbenchSearch = "";
    this.featureSearch = "";
    this.userRoles = [];
    this.availableUsers = [];
    this.isSummary = true;
    this.isShowPermission = false;
    this.isModules = false;
    this.isWorkbench = false;
    this.isFeatureModule = false;
    this.isExistingUsersLoading = true;
    this.isAvailableUsersLoading = true;
    this.cdr.detectChanges();

    this.rolesService.GetUserAccessRoles(this.role_information.id)
      .then((response: any) => {
        this.isExistingUsersLoading = false;
        const data = response.data || response;
        this.userCountForRole = data.length;
        this.userRoles = JSON.parse(JSON.stringify(data));
        this.getAvailableUsers(this.userRoles);
        this.cdr.detectChanges();
      });
  }

getAvailableUsers(existing_user_roles: any[]): void {
  this.isAvailableUsersLoading = true;
  this.usersService.GetUser()
    .then((response: any) => {
      this.isAvailableUsersLoading = false;
      this.users = response.data || response;
      const usersMap: any = {};
      
      // Mark users that are already assigned
      for (let i = 0; i < existing_user_roles.length; i++) {
        if (usersMap[existing_user_roles[i]['user_id']] === undefined) {
          usersMap[existing_user_roles[i]['user_id']] = existing_user_roles[i];
        }
      }
      
      // Clear availableUsers before adding to prevent duplicates
      this.availableUsers = [];
      
      // Track added user IDs to prevent duplicates in availableUsers
      const addedUserIds = new Set();
      
      for (let i = 0; i < this.users.length; i++) {
        const userId = this.users[i]['id'];
        // Check if user is not assigned AND not already added to availableUsers
        if (!usersMap[userId] && !addedUserIds.has(userId)) {
          this.availableUsers.push(this.users[i]);
          addedUserIds.add(userId);
        }
      }
      this.cdr.detectChanges();
    })
    .catch((error: any) => {
      this.isAvailableUsersLoading = false;
      console.error('Error loading users:', error);
    });
}

  assignAvailableUserToRole(available_user: any): void {
    this.isUsersLoaded = false;
    const user_role: any = {};
    user_role.user_id = available_user.id;
    user_role.role_id = this.role_information.id;
    this.rolesService.InsertUserRole(user_role)
      .then(() => {
        this.loadUsersByRoleId();
        this.message = 'User added to role';
        this.isUsersLoaded = true;
        this.autoDismissMessages();
      })
      .catch((error: any) => {
        this.errorMessage = 'Unable to add user to role';
        this.isUsersLoaded = true;
        this.autoDismissMessages();
        console.error(error);
      });
  }

  removeUserRole(user_role: any): void {
    this.isUsersLoaded = false;
    this.rolesService.DeleteUserRole(user_role)
      .then(() => {
        this.message = 'User removed from role';
        this.loadUsersByRoleId();
        this.isUsersLoaded = true;
        this.autoDismissMessages();
      })
      .catch((error: any) => {
        this.isUsersLoaded = true;
        this.errorMessage = 'Unable to remove user';
        this.autoDismissMessages();
        console.error(error);
      });
  }
  getFilteredExistingUsers(): any[] {
    let filtered = this.userRoles;
    if (this.existingUserSearch) {
      const term = this.existingUserSearch.toLowerCase();
      filtered = filtered.filter((u: any) => u.name && u.name.toLowerCase().includes(term));
    }
    return [...filtered].sort((a: any, b: any) => {
      const valA = a.name ? String(a.name).toLowerCase() : '';
      const valB = b.name ? String(b.name).toLowerCase() : '';
      if (valA < valB) return this.existingUserSortReverse ? 1 : -1;
      if (valA > valB) return this.existingUserSortReverse ? -1 : 1;
      return 0;
    });
  }

  getFilteredAvailableUsers(): any[] {
    let filtered = this.availableUsers;
    if (this.availableUserSearch) {
      const term = this.availableUserSearch.toLowerCase();
      filtered = filtered.filter((u: any) => u.name && u.name.toLowerCase().includes(term));
    }
    return [...filtered].sort((a: any, b: any) => {
      const valA = a.name ? String(a.name).toLowerCase() : '';
      const valB = b.name ? String(b.name).toLowerCase() : '';
      if (valA < valB) return this.availableUserSortReverse ? 1 : -1;
      if (valA > valB) return this.availableUserSortReverse ? -1 : 1;
      return 0;
    });
  }

  // ========================================================================
  // PERMISSIONS TAB
  // ========================================================================

  loadRolePermissionsByRoleId(resetIndx?: boolean): void {

    this.existingUserSearch = "";
    this.availableUserSearch = "";
    this.accessModuleSearch = "";
    this.accessWorkbenchSearch = "";
    this.featureSearch = "";
    this.role_permissions = [];
    this.isLoadingPermission = true;
    // this.errorMessage = null;
    //this.message = null;
    this.isShowPermission = true;
    this.isSummary = false;
    this.isModules = false;
    this.isWorkbench = false;
    this.isFeatureModule = false;
    this.cdr.detectChanges();
    if (resetIndx) {
      this.currentIndex = null;
    }
    this.rolesService.GetRolePermission(this.role_information.id)
      .then((response: any) => {
        this.assigned_permissions = response.data || response;
        this.getAccessPermission(this.assigned_permissions);
      })
      .catch((error: any) => {
        console.error('Error loading role permissions:', error);
      });
  }

  getAccessPermission(assigned_permissions: any[]): void {
    this.rolesService.GetAllEntities()
      .then((entities: any) => {
        this.isLoadingPermission = false;
        const entitiesList = Array.isArray(entities) ? entities : (entities.data || entities);
        const role_permission_map: any = {};

        for (const role_permission of assigned_permissions) {
          if (role_permission_map[role_permission.entity_id] === undefined) {
            role_permission_map[role_permission.entity_id] = role_permission;
            role_permission_map[role_permission.entity_id][role_permission.permission] = 1;
            role_permission_map[role_permission.entity_id][role_permission.permission + '_permission_id'] = role_permission.id;
          } else {
            role_permission_map[role_permission.entity_id][role_permission.permission] = 1;
            role_permission_map[role_permission.entity_id][role_permission.permission + '_permission_id'] = role_permission.id;
          }
        }

        for (const entity of entitiesList) {
          if (role_permission_map[entity.id]) {
            this.role_permissions.push(role_permission_map[entity.id]);
          } else {
            this.role_permissions.push(entity);
          }
        }

        for (const record of this.role_permissions) {
          if (record.entity_id) {
            record.unique_id = record.entity_id;
          } else {
            record.unique_id = record.id;
          }
        }
        this.cdr.detectChanges();
      })
      .catch((error: any) => {
        console.error('Error loading entities:', error);
      });
  }

  addPermission(value: any, permission: any, access: string, index: number): void {
    this.currentIndex = permission.unique_id;

    if (value === 1) {
      if (permission.entity_id === undefined) {
        permission.entity_id = permission.id;
      }
      permission.role_id = this.role_information.id;
      permission.permission = access;

      this.rolesService.CreateRolePermission(permission)
        .then(() => {
          // Set success message
          this.message = 'Permission added successfully';
          this.cdr.detectChanges();

          // Reload permissions (won't clear message now)
          this.loadRolePermissionsByRoleId(false);

          // Clear message after 3 seconds
          setTimeout(() => {
            if (this.message === 'Permission added successfully') {
              this.message = null;
              this.cdr.detectChanges();
            }
          }, 2000);

          // Handle Cloud Cart sync
          if ((permission.entity == 'Cloud Cart' || permission.entity == 'Cloud Cart Item') &&
            (permission.permission == 'create' || permission.permission == 'update')) {
            let obj: any;
            if (permission.entity == 'Cloud Cart') {
              obj = { value: 1, permission_id: (permission.permission == 'create') ? 2 : 4, role_id: permission.role_id };
            } else {
              obj = { value: 1, permission_id: 5, role_id: permission.role_id };
            }
            this.rolesService.InsertCartRolePermission(obj).then(() => { });
          }
        })
        .catch((error: any) => {
          this.errorMessage = 'Unable to update permission';
          this.cdr.detectChanges();
          console.error(error);

          setTimeout(() => {
            this.errorMessage = null;
            this.cdr.detectChanges();
          }, 2000);
        });
    } else {
      permission.permission = access;
      if ((permission.entity == 'Cloud Cart' || permission.entity == 'Cloud Cart Item') &&
        (permission.permission == 'create' || permission.permission == 'update')) {
        let obj: any;
        if (permission.entity == 'Cloud Cart') {
          obj = { value: 2, permission_id: (permission.permission == 'create') ? 2 : 4, role_id: permission.role_id };
        } else {
          obj = { value: 2, permission_id: 5, role_id: permission.role_id };
        }
        this.rolesService.DeleteCartRolePermission(obj).then(() => { });
      }
      this.removePermission(permission, access);
    }
  }

  removePermission(role_permission: any, access: string): void {
    const remove_permission_id = role_permission[access + '_permission_id'];

    this.rolesService.DeleteRolePermission(remove_permission_id)
      .then(() => {
        // Set success message
        this.message = 'Permission removed successfully';
        this.cdr.detectChanges();

        // Reload permissions (won't clear message now)
        this.loadRolePermissionsByRoleId(false);

        // Clear message after 3 seconds
        setTimeout(() => {
          if (this.message === 'Permission removed successfully') {
            this.message = null;
            this.cdr.detectChanges();
          }
        }, 2000);
      })
      .catch((error: any) => {
        this.errorMessage = 'Unable to remove permission';
        this.cdr.detectChanges();
        console.error(error);

        setTimeout(() => {
          this.errorMessage = null;
          this.cdr.detectChanges();
        }, 3000);
      });
  }

  getFilteredPermissions(): any[] {
    let filtered = this.role_permissions;
    if (this.permissionSearch) {
      const term = this.permissionSearch.toLowerCase();
      filtered = filtered.filter((p: any) =>
        (p.entity && p.entity.toLowerCase().includes(term)) ||
        (p.permission && p.permission.toLowerCase().includes(term)) ||
        (p.prefix && p.prefix.toLowerCase().includes(term))
      );
    }
    return filtered.sort((a: any, b: any) => {
      const valA = a.entity ? String(a.entity).toLowerCase() : '';
      const valB = b.entity ? String(b.entity).toLowerCase() : '';
      if (valA < valB) return this.permissionSortReverse ? 1 : -1;
      if (valA > valB) return this.permissionSortReverse ? -1 : 1;
      return 0;
    });
  }

  // ========================================================================
  // CLOUD CART PERMISSIONS
  // ========================================================================

  loadCartByRoleId(): void {
    this.rolesService.GetAccessRoleCart(this.role_information.id)
      .then((response: any) => {
        this.assigned_cart_permissions = response.data || response;
        this.getCartAccessPermission(this.assigned_cart_permissions);
      })
      .catch((error: any) => {
        console.error('Error loading cart permissions:', error);
      });
  }

  getCartAccessPermission(assigned_cart_permissions: any[]): void {
    this.rolesService.GetAccessCartPermissions()
      .then((cart_permissions: any) => {
        const cartPermsData = cart_permissions.data || cart_permissions;
        const cart_role_permission_map: any = {};
        this.cart_role_permissions = [];

        for (const rp of assigned_cart_permissions) {
          cart_role_permission_map[rp.permission_id] = rp;
        }

        for (const cp of cartPermsData) {
          if (cart_role_permission_map[cp.id]) {
            this.cart_role_permissions.push(cart_role_permission_map[cp.id]);
          } else {
            cp.permission_id = cp.id;
            cp.value = 0;
            this.cart_role_permissions.push(cp);
          }
        }
        this.cdr.detectChanges();
      })
      .catch((error: any) => {
        console.error('Error loading cart access permissions:', error);
      });
  }

  addCartPermission(cart_access: any): void {
    this.cart_message = null;
    this.cart_errorMessage = null;
    cart_access.role_id = this.role_information.id;
    this.rolesService.InsertCartRolePermission(cart_access)
      .then((response: any) => {
        this.loadCartByRoleId();
        this.cart_message = response.message || 'Cart permission updated';
      })
      .catch((error: any) => {
        this.cart_errorMessage = 'Unable to update cart permission';
        console.error(error);
      });
    setTimeout(() => {
      this.cart_message = null;
      this.cart_errorMessage = null;
      this.cdr.detectChanges();
    }, 2500);
  }

  // ========================================================================
  // ACCESS MODULES TAB
  // ========================================================================

  getRoleModules(): void {

    this.existingUserSearch = "";
    this.availableUserSearch = "";
    this.permissionSearch = "";
    this.accessWorkbenchSearch = "";
    this.featureSearch = "";
    this.accessModulesList = [];
    this.isLoadingAccessModules = true;
    this.errorMessage = null;
    this.message = null;
    this.isShowPermission = false;
    this.isSummary = false;
    this.isModules = true;
    this.isWorkbench = false;
    this.isFeatureModule = false;

    this.rolesService.GetAccessModulesByRoleId(this.role_information.id)
      .then((response: any) => {
        this.access_modules = response.data || response;
        this.getAccessModules(this.access_modules);
      })
      .catch((error: any) => {
        console.error('Error loading access modules:', error);
      });
  }

  getAccessModules(accessModules: any[]): void {
    this.rolesService.GetAllModules()
      .then((modules: any) => {
        this.isLoadingAccessModules = false;
        const modulesList = Array.isArray(modules) ? modules : (modules.data || modules);
        const modules_map: any = {};

        for (const access_module of accessModules) {
          if (modules_map[access_module.module_id] === undefined) {
            modules_map[access_module.module_id] = access_module;
            modules_map[access_module.module_id][access_module.access] = 1;
          } else {
            modules_map[access_module.module_id][access_module.access] = 1;
          }
        }

        for (const mod of modulesList) {
          if (modules_map[mod.id]) {
            this.accessModulesList.push(modules_map[mod.id]);
          } else {
            this.accessModulesList.push(mod);
          }
        }
        this.cdr.detectChanges();
      })
      .catch((error: any) => {
        console.error('Error loading modules:', error);
      });
  }

  updateModuleAccess(accessModule: any): void {
    const moduleData = this.prepareAccessModuleData(accessModule);
    this.rolesService.InsertAccessModule(moduleData)
      .then(() => {
        this.message = 'Access role modules updated successfully';
        this.cdr.detectChanges();

        if (accessModule.access === 0) {
          this.showNotificationAboutUserSession = true;
          setTimeout(() => {
            this.showNotificationAboutUserSession = false;
            this.cdr.detectChanges();
          }, 7000);
        }

        // Clear message after 3 seconds
        setTimeout(() => {
          this.message = null;
          this.cdr.detectChanges();
        }, 3000);
      })
      .catch((error: any) => {
        this.errorMessage = error?.error?.message || 'Error updating module access';
        this.cdr.detectChanges();
        console.error(error);

        // Clear error after 3 seconds
        setTimeout(() => {
          this.errorMessage = null;
          this.cdr.detectChanges();
        }, 3000);
      });
  }

  prepareAccessModuleData(accessModule: any): any {
    let obj: any = {};
    if (!accessModule.module_id) {
      obj = {
        role_id: this.role_information.id,
        module_id: accessModule.id,
        access: accessModule.access
      };
    } else if (accessModule.module_id && accessModule.id) {
      obj = {
        id: accessModule.id,
        role_id: this.role_information.id,
        module_id: accessModule.module_id,
        access: accessModule.access
      };
    }
    return obj;
  }

  getFilteredModules(): any[] {
    let filtered = this.accessModulesList;
    if (this.accessModuleSearch) {
      const term = this.accessModuleSearch.toLowerCase();
      filtered = filtered.filter((m: any) =>
        m.name && m.name.toLowerCase().includes(term)
      );
    }
    return [...filtered].sort((a: any, b: any) => {
      const valA = a.name ? String(a.name).toLowerCase() : '';
      const valB = b.name ? String(b.name).toLowerCase() : '';
      if (valA < valB) return this.modulesSortReverse ? 1 : -1;
      if (valA > valB) return this.modulesSortReverse ? -1 : 1;
      return 0;
    });
  }

  // ========================================================================
  // ACCESS WORKBENCH TAB
  // ========================================================================

  getAccessWorkbench(): void {

    this.existingUserSearch = "";
    this.availableUserSearch = "";
    this.permissionSearch = "";
    this.accessModuleSearch = "";
    this.featureSearch = "";

    this.accessWorkbenchList = [];
    this.errorMessage = null;
    this.message = null;
    this.isShowPermission = false;
    this.isSummary = false;
    this.isModules = false;
    this.isWorkbench = true;
    this.isFeatureModule = false;
    this.getAccessWorkbenchByRoles();
  }

  getAccessWorkbenchByRoles(): void {
    this.rolesService.GetWorkbenchPermissionsByRole(this.role_information.id)
      .then((response: any) => {
        const data = response.data || response;
        if (data && data.length) {
          this.accessWorkbenchList = [
            { name: 'Prime Admin', access: data[0].admin_workbench },
            { name: 'Prime Business', access: data[0].business_workbench },
            { name: 'Product Explorer Cloudcart', access: data[0].pe_cloudcart }
          ];
        } else {
          this.accessWorkbenchList = [
            { name: 'Prime Admin', access: 0 },
            { name: 'Prime Business', access: 0 },
            { name: 'Product Explorer Cloudcart', access: 0 }
          ];
        }
        this.cdr.detectChanges();
      })
      .catch((error: any) => {
        console.error('Error loading workbench permissions:', error);
      });
  }

  upsertWorkbenchAccess(workbench: any): void {
    const workbenchObject: any = {};
    workbenchObject.role_id = this.role_information.id;
    if (workbench.name.toLowerCase() === 'prime admin') {
      workbenchObject.admin_workbench = workbench.access;
      workbenchObject.business_workbench = this.accessWorkbenchList[1].access;
      workbenchObject.pe_cloudcart = this.accessWorkbenchList[2].access;
    } else if (workbench.name.toLowerCase() === 'prime business') {
      workbenchObject.admin_workbench = this.accessWorkbenchList[0].access;
      workbenchObject.business_workbench = workbench.access;
      workbenchObject.pe_cloudcart = this.accessWorkbenchList[2].access;
    } else if (workbench.name.toLowerCase() === 'product explorer cloudcart') {
      workbenchObject.admin_workbench = this.accessWorkbenchList[0].access;
      workbenchObject.business_workbench = this.accessWorkbenchList[1].access;
      workbenchObject.pe_cloudcart = workbench.access;
    }
    this.rolesService.UpsertWorkbenchAccess(workbenchObject)
      .then(() => {
        this.message = 'Workbench access updated';
        this.cdr.detectChanges();

        // Clear message after 3 seconds
        setTimeout(() => {
          this.message = null;
          this.cdr.detectChanges();
        }, 3000);
      })
      .catch((error: any) => {
        this.errorMessage = 'Unable update workbench access';
        this.cdr.detectChanges();
        console.error(error);

        // Clear error after 3 seconds
        setTimeout(() => {
          this.errorMessage = null;
          this.cdr.detectChanges();
        }, 3000);
      });
  }

  getFilteredWorkbenches(): any[] {
    let filtered = this.accessWorkbenchList;
    if (this.accessWorkbenchSearch) {
      const term = this.accessWorkbenchSearch.toLowerCase();
      filtered = filtered.filter((w: any) =>
        w.name && w.name.toLowerCase().includes(term)
      );
    }
    return [...filtered].sort((a: any, b: any) => {
      const valA = a.name ? String(a.name).toLowerCase() : '';
      const valB = b.name ? String(b.name).toLowerCase() : '';
      if (valA < valB) return this.workbenchSortReverse ? 1 : -1;
      if (valA > valB) return this.workbenchSortReverse ? -1 : 1;
      return 0;
    });
  }

  // ========================================================================
  // FEATURE PERMISSION TAB
  // ========================================================================

  getFeatureAccessModules(): void {

    this.existingUserSearch = "";
    this.availableUserSearch = "";
    this.permissionSearch = "";
    this.accessModuleSearch = "";
    this.accessWorkbenchSearch = "";

    this.featuresList = [];
    this.isLoadingFeatureList = true;
    this.errorMessage = null;
    this.message = null;
    this.isShowPermission = false;
    this.isSummary = false;
    this.isModules = false;
    this.isWorkbench = false;
    this.isFeatureModule = true;

    this.rolesService.FetchAllFeatures()
      .then((featuresResponse: any) => {
        this.featuresList = featuresResponse.data || featuresResponse || [];
        this.rolesService.FetchFeatureRoles(this.role_information.id)
          .then((response: any) => {
            const featureData = response.data || response;
            this.featureAccessMap = {};
            for (let i = 0; i < featureData.length; i++) {
              if (this.featureAccessMap[featureData[i].feature_id] === undefined) {
                this.featureAccessMap[featureData[i].feature_id] = {
                  access: featureData[i].access,
                  allow_featured_password: featureData[i].allow_featured_password
                };
              }
            }
            for (let j = 0; j < this.featuresList.length; j++) {
              if (this.featureAccessMap[this.featuresList[j].id] !== undefined) {
                this.featuresList[j].access = this.featureAccessMap[this.featuresList[j].id].access;
                this.featuresList[j].allow_featured_password = this.featureAccessMap[this.featuresList[j].id].allow_featured_password;
              }
            }
            this.isLoadingFeatureList = false;
            this.cdr.detectChanges();
          });
      })
      .catch((error: any) => {
        console.error('Error loading features:', error);
      });
  }

  upsertFeature(data: any): void {
    const addFeature: any = {};
    addFeature.feature_id = data.id;
    addFeature.role_id = this.role_information.id;
    addFeature.access = data.access;
    // 'allow_featured_password' as false if no 'access'
    addFeature.allow_featured_password = data.access ? data.allow_featured_password : data.access;
    // update model 'allow_featured_password' value
    data.allow_featured_password = addFeature.allow_featured_password;

    this.rolesService.CreateFeatureRoles(addFeature)
      .then(() => {
        this.message = 'Feature access updated';
        this.cdr.detectChanges();
      })
      .catch((error: any) => {
        this.errorMessage = 'Unable update feature access';
        this.cdr.detectChanges();
        console.error(error);
      });
    this.autoDismissMessages();
  }

  getFilteredFeatures(): any[] {
    let filtered = this.featuresList;
    if (this.featureSearch) {
      const term = this.featureSearch.toLowerCase();
      filtered = filtered.filter((f: any) =>
        (f.feature && f.feature.toLowerCase().includes(term)) ||
        (f.feature_code && f.feature_code.toLowerCase().includes(term))
      );
    }
    return [...filtered].sort((a: any, b: any) => {
      const valA = a.feature ? String(a.feature).toLowerCase() : '';
      const valB = b.feature ? String(b.feature).toLowerCase() : '';
      if (valA < valB) return this.featureSortReverse ? 1 : -1;
      if (valA > valB) return this.featureSortReverse ? -1 : 1;
      return 0;
    });
  }

  // ========================================================================
  // CREATE ROLE
  // ========================================================================

  openForm(): void {
    this.message = null;
    this.initRole();
    this.isShowRoleDetails = false;
    this.isShowRole = false;
    this.isCreateRole = true;
    this.isCloneRole = false;
    this.isUpdateRole = false;
    this.resetForm();
    this.cdr.detectChanges();
  }

  // Add this property at the top with other properties
  isSaving = false;

  // Update your save method
  save(role: any): void {
    if (this.isSaving) return; // Prevent multiple submissions

    this.isSaving = true;
    this.saveBtnText = 'Saving Now...';
    role.status_id = 1;
    this.isProcessed = false;
    if (role.is_lsa == true) role.is_lsa = 1; else role.is_lsa = 0;

    this.rolesService.CreateRole(role)
      .then((response: any) => {
        this.isSaving = false;
        this.saveBtnText = 'Save';
        this.isProcessed = true;
        this.isSaveSuccess = true;
        role.id = role.role_id = response.data?.inserted_id || response.inserted_id;
        this.accessRolesList.push(role);
        this.totalRecords = this.accessRolesList.length;
        this.cdr.detectChanges();
      })
      .catch((error: any) => {
        this.isSaving = false;
        this.message = error?.error?.error || 'Error creating role';
        this.isProcessed = true;
        this.saveBtnText = 'Oops.!! Something went wrong';
        this.saveBtnError = true;
        setTimeout(() => {
          this.saveBtnText = 'Save';
          this.saveBtnError = false;
          this.cdr.detectChanges();
        }, 2500);
      });
  }

  createAnotherForm(): void {
    this.role = {};
    this.isCreateRole = true;
    this.isSaveSuccess = false;
  }

  closeForm(): void {
    this.isShowRole = false;
    this.isCreateRole = false;
    this.isUpdateRole = false;
    this.message = null;
    this.saveBtnText = 'Save';
    this.saveBtnError = false;
    setTimeout(() => {
      this.isSaveSuccess = false;
      this.cdr.detectChanges();
    }, 500);
  }

  // ========================================================================
  // CLONE ROLE
  // ========================================================================

  openClone(oldRole: any): void {
    this.message = null;
    this.initRole();
    this.isShowRoleDetails = false;
    this.isCloneRole = true;
    this.isShowRole = false;
    this.isUpdateRole = false;
    this.old_role_id = oldRole.id;
    this.resetForm();
    this.cdr.detectChanges();
  }

  clone(role: any): void {
    this.cloneBtnTxt = 'Cloning...';
    role.status_id = 1;
    if (role.is_lsa == true) role.is_lsa = 1; else role.is_lsa = 0;
    this.isProcessed = false;
    role.old_role_id = this.old_role_id;
    this.rolesService.CloneRole(role)
      .then((response: any) => {
        this.cloneBtnTxt = 'Clone';
        this.isProcessed = true;
        this.isCloneSuccess = true;
        this.old_role_id = null;
        role.id = role.role_id = response.data?.inserted_id || response.inserted_id;
        this.accessRolesList.push(role);
        this.totalRecords = this.accessRolesList.length;
        this.cdr.detectChanges();
      })
      .catch((error: any) => {
        this.message = error?.error?.error || 'Error cloning role';
        this.isProcessed = true;
        this.cloneBtnTxt = 'Oops.!! Something went wrong';
        this.cloneBtnError = true;
        setTimeout(() => {
          this.cloneBtnTxt = 'Clone';
          this.cloneBtnError = false;
          this.old_role_id = null;
          this.cdr.detectChanges();
        }, 2500);
      });
  }

  closeCloneForm(): void {
    this.message = null;
    this.isCloneRole = false;
    this.cloneBtnTxt = 'Clone';
    this.old_role_id = null;
    this.cloneBtnError = false;
    setTimeout(() => {
      this.isCloneSuccess = false;
      this.cdr.detectChanges();
    }, 500);
  }

  // ========================================================================
  // UPDATE ROLE
  // ========================================================================

  openUpdateProfilePanel(): void {
    this.roleDetails = JSON.parse(JSON.stringify(this.role_information));
    this.oldRoleDetails = JSON.parse(JSON.stringify(this.role_information));
    this.isShowRoleDetails = false;
    this.isShowRole = false;
    this.isCloneRole = false;
    this.updateBtnText = 'Update';
    this.isUpdateRole = true;
    this.isUpdateSuccess = false;
    this.isCreateRole = false;
    this.cdr.detectChanges();
  }

  closeUpdateForm(): void {
    this.isShowRole = false;
    this.isCloneRole = false;
    this.updateBtnText = 'Update';
    this.saveBtnText = 'Save';
    this.updateBtnError = false;
    this.saveBtnError = false;
    this.isUpdateRole = false;
    setTimeout(() => {
      this.isUpdateSuccess = false;
      this.isSaveSuccess = false;
      this.cdr.detectChanges();
    }, 500);
  }

  hasUpdateChanges(payload: any): boolean {
    for (const key in payload) {
      if (
        key.toLowerCase() !== 'id' &&
        key.toLowerCase() !== 'user_id' &&
        key.toLowerCase() !== 'details_id' &&
        this.oldRoleDetails[key] !== undefined &&
        payload[key] !== undefined &&
        this.oldRoleDetails[key] !== payload[key] &&
        (typeof this.oldRoleDetails[key] !== 'object' || typeof payload[key] !== 'object')
      ) {
        return true;
      }
    }
    return false;
  }



  updateRole(role_details: any): void {
    role_details.id = this.roleDetails.id;
    if (role_details.is_lsa == true) role_details.is_lsa = 1; else role_details.is_lsa = 0;
    if (this.hasUpdateChanges(role_details)) {
      this.isUpdating = true; // Add this
      this.isProcessed = false;
      this.updateBtnText = 'Updating Now...';
      this.rolesService.UpdateRole(role_details)
        .then(() => {
          this.isUpdating = false; // Add this
          this.updateBtnText = 'Done';
          this.isUpdateSuccess = true;
          this.isProcessed = true;
          this.getAccessRoles(undefined, false);
          this.cdr.detectChanges();
        })
        .catch((error: any) => {
          this.isUpdating = false; // Add this
          this.updateUserMessage = error?.error?.error || 'Error updating role';
          this.updateBtnText = 'Oops.!! Something went wrong';
          this.updateBtnError = true;
          this.isProcessed = true;
          setTimeout(() => {
            this.updateBtnText = 'Update';
            this.updateBtnError = false;
            this.updateUserMessage = null;
            this.cdr.detectChanges();
          }, 3500);
        });
    } else {
      this.updateBtnText = 'Nothing to update';
      this.updateBtnError = true;
      setTimeout(() => {
        this.updateBtnText = 'Update';
        this.updateBtnError = false;
        this.cdr.detectChanges();
      }, 1000);
    }
  }

  // ========================================================================
  // DELETE ROLE
  // ========================================================================

  showDeletePopup(roleInfo: any): void {
    this.roleInfo = roleInfo;
    this.roleDeleteErrorMessage = null;
    this.hasRoleDeleted = false;
    this.enableDeleteUser = true;
  }

  cancelDeleteRole(): void {
    this.roleInfo = null;
    this.roleDeleteErrorMessage = null;
    this.hasRoleDeleted = false;
    this.enableDeleteUser = false;
  }

  deleteRole(roleInfo: any): void {
    if (this.userCountForRole === 0) {
      this.rolesService.DeleteRole(roleInfo)
        .then(() => {
          this.hasRoleDeleted = true;

          // Set success message
          this.message = `Role "${roleInfo.role}" has been successfully deleted.`;

          setTimeout(() => {
            this.roleInfo = null;
            this.enableDeleteUser = false;
          }, 1500);

          setTimeout(() => {
            this.roleDeleteErrorMessage = null;
            // Don't clear message immediately - let it show
            // this.message = null; // Remove this line
            this.errorMessage = null;
            this.isShowRoleDetails = false;
            this.isShowPermission = false;
            this.isModules = false;
            this.isWorkbench = false;
            this.isFeatureModule = false;
            this.isSaveSuccess = false;
            this.role_information = {};
            this.getAccessRoles();

            // Clear message after showing
            setTimeout(() => {
              this.message = null;
              this.cdr.detectChanges();
            }, 3000);

            this.cdr.detectChanges();
          }, 1700);
        })
        .catch(() => {
          this.roleDeleteErrorMessage = 'Cannot be deleted. Role is referenced in Access role permission.';
          setTimeout(() => {
            this.roleInfo = null;
            this.enableDeleteUser = false;
            this.cdr.detectChanges();
          }, 2500);
        });
    } else {
      this.roleDeleteErrorMessage = 'Cannot be deleted, Role associates to the users.';
      setTimeout(() => {
        this.roleInfo = null;
        this.enableDeleteUser = false;
        this.cdr.detectChanges();
      }, 2500);
    }
  }

  // ========================================================================
  // HELPERS
  // ========================================================================

  initRole(): void {
    this.role = {};
  }

  resetForm(): void {
    this.role = {};
    this.role.role = null;
    this.role.code = null;
    this.role.details = null;
  }

  autoDismissMessages(): void {
    setTimeout(() => {
      this.message = null;
      this.errorMessage = null;
      this.cdr.detectChanges();
    }, 2500);
  }

  // Check if create form is valid
isFormInvalid(): boolean {
    const trimmedRole = this.role.role ? this.role.role.trim() : '';
    const trimmedCode = this.role.code ? this.role.code.trim() : '';
    const trimmedDetails = this.role.details ? this.role.details.trim() : '';
    
    return !trimmedRole ||
      trimmedRole.length < 2 ||
      trimmedRole.length > 40 ||
      !trimmedCode ||
      trimmedCode.length < 2 ||
      trimmedCode.length > 10 ||
      !trimmedDetails ||
      trimmedDetails.length < 2 ||
      trimmedDetails.length > 40;
}
  isUpdateFormInvalid(): boolean {
    const trimmedRole = this.roleDetails?.role ? this.roleDetails?.role.trim() : '';
    const trimmedCode = this.roleDetails?.code ? this.roleDetails?.code.trim() : '';
    const trimmedDetails = this.roleDetails?.details ? this.roleDetails?.details.trim() : '';

    return !trimmedRole ||
      trimmedRole.length < 2 ||
      trimmedRole.length > 40 ||
      !trimmedCode ||
      trimmedCode.length < 2 ||
      trimmedCode.length > 10 ||
      !trimmedDetails ||
      trimmedDetails.length < 2 ||
      trimmedDetails.length > 40;
  }
}
